import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouterLegacy } from 'core/routerUtils';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import {
  IrregularityType,
  IrregularityDetails,
  IrregularityModel,
  IrregularityInvolvement,
  IrregularityNotes,
  IrregularitiesDashboard,
} from 'features/irregularities/index';
import { Stepper, Subheader, SuccessScreen, Spinner } from 'shared/components';
import * as irregularitiesActions from 'features/irregularities/duck';
import * as staticActions from 'shared/ducks/staticDuck';
import * as tcEventActions from 'features/tc-event-dashboard/duck';
import * as roomActions from 'features/room-builder/duck';
import * as staffActions from 'features/staff-and-rooms/duck';
import isEmpty from 'lodash/isEmpty';
import './IrregularitiesContainer.scss';
import { getCategoriesFilteredTypes } from 'features/irregularities/selectors';
import { PermissionsService } from 'shared/services';
const basePath = '/irregularities';

const getStep = (props, steps) => {
  const { step } = props.match.params;
  return steps.findIndex((s) => s.name === step);
};

const getUrl = (stepUrl, eventId, roomId) =>
  `${basePath}${stepUrl}/${eventId}${roomId ? `/${roomId}` : ''}`;

class IrregularitiesContainer extends Component {
  constructor(props) {
    super(props);

    this.nextStep = this.nextStep.bind(this);
    this.prevStep = this.prevStep.bind(this);
    this.renderIrregularities = this.renderIrregularities.bind(this);
    this.renderCategories = this.renderCategories.bind(this);
    this.renderDetails = this.renderDetails.bind(this);
    this.renderInvolvement = this.renderInvolvement.bind(this);
    this.renderIrregularityNotes = this.renderIrregularityNotes.bind(this);
    this.renderSuccessScreen = this.renderSuccessScreen.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onConfirm = this.onConfirm.bind(this);
    this.setStartPosition = this.setStartPosition.bind(this);

    this.BUILD_STEPS = [
      {
        id: 0,
        render: this.renderIrregularities,
        url: '/all',
        name: 'all',
      },
      {
        id: 1,
        render: this.renderCategories,
        url: '/type',
        name: 'type',
      },
      {
        id: 2,
        render: this.renderDetails,
        url: '/details',
        name: 'details',
      },
      {
        id: 3,
        render: this.renderInvolvement,
        url: '/involvement',
        name: 'involvement',
      },
      {
        id: 4,
        render: this.renderIrregularityNotes,
        url: '/notes',
        name: 'notes',
      },
      {
        id: 5,
        render: this.renderSuccessScreen,
        url: '/success',
        name: 'success',
      },
    ];

    const { params } = this.props.match;
    let step = this.BUILD_STEPS.findIndex((bStep) => bStep.url === `/${params.step}`);
    let startedIrregularity = JSON.parse(sessionStorage.getItem('irregularity'));
    const room = JSON.parse(sessionStorage.getItem('room'));

    if (step === -1 || !startedIrregularity) {
      step = 0;
    }

    if ((room || params.roomId) && !startedIrregularity) {
      startedIrregularity = new IrregularityModel({
        irregularityRooms: [{ roomId: params.roomId || room.id }],
      });
    }

    this.state = {
      step,
      irregularity: startedIrregularity || new IrregularityModel({}),
      errorIrregularity: {},
    };
  }

  componentDidMount() {
    const { params } = this.props.match;
    this.props.actions.getIrregularityCategories();
    this.props.actions.getRooms(params.eventId);

    this.props.actions.getTcEvent(params.eventId).then(() => {
      if (
        PermissionsService.hasTestCenterManage(this.props.tcEvent.testCenterCycleId) ||
        PermissionsService.hasEventManage(params.eventId) ||
        PermissionsService.hasRoomAccess(params.eventId)
      ) {
        this.props.actions.getAllRoomsDTO(params.eventId);
      } else {
        this.props.actions.getRoomDTO(params.roomId);
      }
      if (this.props.tcEvent.testingEvent?.adminId?.cycleId?.programId?.id) {
        this.props.actions.getIrregularityStages({
          programId: this.props.tcEvent.testingEvent?.adminId?.cycleId?.programId?.id,
        });
      }
      this.props.actions.getTcEventStaff(params.eventId);
      this.props.actions.getTestCenterCycle(params.eventId);
    });
    this.props.actions.getServerDateTime();
    if (params.roomId) {
      this.props.actions.getRoomIrregularities(params.roomId);
    } else {
      this.props.actions.getTcEventIrregularities(params.eventId);
    }
  }

  componentWillUnmount() {
    sessionStorage.removeItem('irregularity');
    sessionStorage.removeItem('room');
    this.props.actions.getTcEventRegsAction({ data: [] });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.step !== this.props.match.params.step) {
      this.setState({
        step: getStep(this.props, this.BUILD_STEPS),
      });
    }
  }

  nextStep(info, isDirty) {
    const { params } = this.props.match;
    const step = this.state.step + 1;
    let nextStep = this.BUILD_STEPS[step];
    let irregularity;

    if (this.state.step === 0) {
      irregularity = new IrregularityModel({
        irregularityRooms: params.roomId ? [{ roomId: params.roomId }] : [],
      });
    } else if (this.state.step === 1) {
      irregularity = isDirty
        ? new IrregularityModel({
            irregularityRooms: params.roomId ? [{ roomId: params.roomId }] : [],
            irregularityType: info.irregularityType,
          })
        : { ...this.state.irregularity, ...info };
    } else {
      irregularity = { ...this.state.irregularity, ...info };
    }

    if (step > 1 && step < 6) {
      sessionStorage.setItem('irregularity', JSON.stringify(irregularity));
    }

    if (nextStep.id === 5) {
      const submitPersonnel = { ...this.props.user };
      const { testCenterCyclePersonnels, testCenterEventPersonnels, ...updatedPersonnel } =
        submitPersonnel;
      irregularity.submitPersonnel = updatedPersonnel;

      irregularity.testCenterEventId = this.props.match.params.eventId;
      nextStep = this.BUILD_STEPS[step];
      irregularity.irregularityRooms = irregularity.irregularityRooms.filter((r) => r.roomId > 0);

      this.props.actions.setIrregularity(irregularity).then(() => {
        if (!isEmpty(this.props.errorIrregularity)) {
          this.setState({
            errorIrregularity: this.props.errorIrregularity,
          });
          return;
        }
        const room = JSON.parse(sessionStorage.getItem('room'));

        if (params.roomId) {
          this.props.actions.getRoomIrregularities(params.roomId);
        } else {
          this.props.actions.getTcEventIrregularities(params.eventId);
        }

        if (room && !room.added) {
          room.added = true;
          sessionStorage.setItem('room', JSON.stringify(room));
          sessionStorage.removeItem('irregularity');
          this.props.history.go(-(this.BUILD_STEPS.length - 2));
          return;
        }
        this.props.history.push(getUrl(nextStep.url, params.eventId, params.roomId));
      });

      this.setState({
        step,
        irregularity,
        errorIrregularity: {},
      });

      return;
    }

    this.setState({
      step,
      irregularity,
      errorIrregularity: {},
    });

    this.props.history.push(
      getUrl(nextStep.url, this.props.match.params.eventId, this.props.match.params.roomId)
    );
  }

  prevStep() {
    if (this.state.step > 0) {
      const prevStep = this.state.step - 1;
      this.setState({
        step: prevStep,
      });
      this.props.history.goBack();
    }
  }

  onCancel() {
    this.setState({
      step: 0,
      irregularity: new IrregularityModel({}),
    });

    sessionStorage.removeItem('irregularity');
    this.props.history.go(-(this.BUILD_STEPS.length - 2));
  }

  renderIrregularities() {
    const { params } = this.props.match;
    const { irregularityCategories, irregularityStages, history, tcEvent, tcCycle } = this.props;
    const irregularities = params.roomId
      ? this.props.roomIrregularities
      : this.props.irregularities;
    return !isEmpty(tcEvent) &&
      !isEmpty(irregularityCategories) &&
      !isEmpty(irregularityStages) &&
      !isEmpty(tcCycle) ? (
      <IrregularitiesDashboard
        irregularities={irregularities}
        irregularityCategories={irregularityCategories}
        irregularityStages={irregularityStages}
        tcEvent={tcEvent}
        roomId={params.roomId}
        allRoomsDTO={this.props.allRoomsDTO}
        startPosition={this.state.startPosition}
        setStartPosition={this.setStartPosition}
        onBack={history.goBack}
        onCreate={this.nextStep}
        tcCycle={tcCycle}
      />
    ) : (
      <Spinner />
    );
  }

  renderCategories() {
    return (
      <IrregularityType
        irregularity={this.state.irregularity}
        irregularityCategories={this.props.irregularityCategories}
        onContinue={this.nextStep}
        onBack={this.prevStep}
      />
    );
  }

  renderDetails() {
    return (
      <IrregularityDetails
        irregularity={this.state.irregularity}
        irregularityStages={this.props.irregularityStages}
        roomId={this.props.match.params.roomId}
        onContinue={this.nextStep}
        onBack={this.prevStep}
      />
    );
  }

  renderInvolvement() {
    const { params } = this.props.match;
    const { tcCycle } = this.props;

    return (
      !isEmpty(tcCycle) && (
        <IrregularityInvolvement
          irregularity={this.state.irregularity}
          rooms={this.props.rooms}
          tcCycle={tcCycle}
          disableRoom={!!this.props.match.params.roomId}
          eventId={params.eventId}
          onContinue={this.nextStep}
          onBack={this.prevStep}
        />
      )
    );
  }

  renderIrregularityNotes() {
    return (
      <IrregularityNotes
        irregularity={this.state.irregularity}
        submitError={this.state.errorIrregularity}
        onContinue={this.nextStep}
        onCancel={this.onCancel}
        onBack={this.prevStep}
      />
    );
  }

  onConfirm() {
    this.props.history.go(-(this.BUILD_STEPS.length - 1));
  }

  setStartPosition(position) {
    this.setState({
      startPosition: position,
    });
  }

  renderSuccessScreen() {
    const successData = {
      copy: 'irregularities.successCopy',
      copyDynamic: {
        irregularity: this.state.irregularity.irregularityType.irregularityCategory.name,
      },
      buttonLabel: 'irregularities.continueToLogged',
      callback: this.onConfirm,
    };
    sessionStorage.removeItem('irregularity');
    return <SuccessScreen data={successData} />;
  }

  render() {
    const { step } = this.state;
    const currentStepConfig = this.BUILD_STEPS[step];

    return (
      <div className="irregularities-container">
        {step > 0 && step < 5 && (
          <Subheader>
            <div className="steps-container">
              <span className="text-strong">{this.props.t('irregularities.newIrregularity')}</span>
            </div>
          </Subheader>
        )}
        <div className="irregularities-subcontainer">
          {step > 0 && step < 5 && (
            <Stepper
              current={step - 1}
              stepsTotal={this.BUILD_STEPS.slice(2).map(({ render: _render, ...o }) => o)}
            />
          )}
          <div className="steps-container mt-3">{currentStepConfig?.render.call(this)}</div>
        </div>
      </div>
    );
  }
}

IrregularitiesContainer.propTypes = {
  match: PropTypes.shape({
    url: PropTypes.string.isRequired,
    params: PropTypes.shape({
      id: PropTypes.string,
      step: PropTypes.string,
      eventId: PropTypes.string,
      roomId: PropTypes.string,
    }).isRequired,
  }).isRequired,
  actions: PropTypes.shape({
    getTcEventIrregularities: PropTypes.func.isRequired,
    getIrregularityCategories: PropTypes.func.isRequired,
    getIrregularityStages: PropTypes.func.isRequired,
    setIrregularity: PropTypes.func.isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    goBack: PropTypes.func.isRequired,
  }).isRequired,
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  tcEvent: PropTypes.shape({}).isRequired,
  irregularities: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  irregularityCategories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  irregularityStages: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  t: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    user: state.users.currentUser,
    irregularities: state.irregularities.eventIrregularities || [],
    errorIrregularity: state.irregularities.errorIrregularity,
    roomIrregularities: state.irregularities.roomIrregularities || [],
    irregularityCategories: getCategoriesFilteredTypes(state) || [],
    irregularityStages: state.statics.irregularityStages || [],
    rooms: state.tcEvent.rooms || [],
    allRoomsDTO: !isEmpty(state.tcEvent.allRoomsDTO)
      ? state.tcEvent.allRoomsDTO
      : state.roomOptions.roomDTO,
    tcEvent: state.tcEvent.currentEvent || {},
    tcCycle: state.tcEvent.testCenterCycle,
    serverDateTime: state.statics.serverDateTime,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...irregularitiesActions,
        ...staticActions,
        ...roomActions,
        ...tcEventActions,
        ...staffActions,
      },
      dispatch
    ),
  };
}

export default withTranslation()(
  withRouterLegacy(connect(mapStateToProps, mapDispatchToProps)(IrregularitiesContainer))
);
